// ./src/hooks/useTrackingUploader.ts
//
// Manages the buffer-and-flush cycle for sending filtered GPS points
// to the server via TravelTrackingService.addPoints().
//
// Responsibilities:
// - Accept filtered points from useTrackingFilter via addPoint()
// - Buffer points in memory, mirrored to IndexedDB for crash resilience
// - Flush buffer to server every FLUSH_INTERVAL_MS (10s) or when buffer
//   reaches MAX_BUFFER_SIZE (20 points)
// - On flush failure, retain points and retry with exponential backoff
// - On success, clear flushed points from buffer and IndexedDB
// - Provide flushNow() for synchronous drain before pause/complete
//
// This hook does NOT:
// - Collect GPS readings (that's useGpsTracker)
// - Decide which points to keep (that's useTrackingFilter)
// - Manage the tracking lifecycle (that's useTrackingSession)

import { useRef, useCallback, useEffect } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import { type BufferedTrackingPoint } from "@/contexts/TrackingStorageContext/TrackingStorageContext";
import { useTrackingStorage } from "@/contexts/TrackingStorageContext/TrackingStorageContext";
import { TravelTrackingService, type TrackingPointInput } from "@/utils/api";
import {
  FLUSH_INTERVAL_MS,
  MAX_BUFFER_SIZE,
  MAX_BATCH_SIZE,
  UPLOAD_BACKOFF_SCHEDULE,
} from "@/config/trackingConstants";

// ============================================
// Types
// ============================================

export interface UseTrackingUploaderProps {
  /** The travel ID to upload points for. Null when not active. */
  travelId: string | null;
  /** Whether the uploader should be running (flush timer active) */
  enabled: boolean;
  /**
   * Called after each successful flush with the number of points uploaded.
   * Useful for updating UI counters.
   */
  onFlushSuccess?: (pointCount: number) => void;
  /**
   * Called after a flush failure with the error message.
   * Useful for showing upload status indicators.
   */
  onFlushError?: (error: string) => void;
}

export interface UseTrackingUploaderReturn {
  /**
   * Add a filtered point to the upload buffer.
   * Called by useTrackingFilter's onPointKept callback.
   * Triggers an immediate flush if buffer reaches MAX_BUFFER_SIZE.
   */
  addPoint: (point: BufferedTrackingPoint) => void;

  /**
   * Flush all buffered points to the server immediately.
   * Returns a promise that resolves when the flush completes
   * (success or failure). Used before pause/complete to ensure
   * all points are uploaded.
   *
   * @param gpsDistance - Current GPS distance to send with the batch
   * @returns true if all points were flushed successfully
   */
  flushNow: (gpsDistance: number) => Promise<boolean>;

  /**
   * Get the current number of buffered (pending) points.
   */
  getBufferSize: () => number;

  /**
   * Restore buffer from IndexedDB (for session recovery).
   * Returns the restored points.
   */
  restoreBuffer: () => Promise<BufferedTrackingPoint[]>;

  /**
   * Clear the buffer completely (for discard or after complete).
   */
  clearBuffer: () => Promise<void>;

  /**
   * Whether an upload is currently in progress.
   */
  isUploading: () => boolean;

  /**
   * Set the GPS distance value to include in the next flush.
   * Called by the session manager whenever gpsDistance changes.
   */
  setGpsDistance: (distance: number) => void;
}

// ============================================
// Hook
// ============================================

/**
 * Buffer-and-flush uploader for GPS tracking points.
 *
 * Points are buffered in memory and mirrored to IndexedDB.
 * Every FLUSH_INTERVAL_MS, the buffer is sent to the server.
 * On failure, points are retained and retried with backoff.
 *
 * @example
 * ```tsx
 * const {
 *   addPoint,
 *   flushNow,
 *   clearBuffer,
 *   setGpsDistance,
 * } = useTrackingUploader({
 *   travelId: activeTravelId,
 *   enabled: isTracking,
 *   onFlushSuccess: (count) => console.log(`Uploaded ${count} points`),
 * });
 *
 * // In useTrackingFilter's onPointKept callback:
 * addPoint(filteredPoint);
 *
 * // Before pausing:
 * await flushNow(currentGpsDistance);
 *
 * // On discard:
 * await clearBuffer();
 * ```
 */
export const useTrackingUploader = ({
  travelId,
  enabled,
  onFlushSuccess,
  onFlushError,
}: UseTrackingUploaderProps): UseTrackingUploaderReturn => {
  const storage = useTrackingStorage();

  // ── Refs for all mutable state (no React state = no re-renders) ──

  /** The in-memory point buffer */
  const bufferRef = useRef<BufferedTrackingPoint[]>([]);

  /** Current GPS distance to include with uploads */
  const gpsDistanceRef = useRef<number>(0);

  /** Whether a flush is currently in progress (prevents concurrent flushes) */
  const isFlushingRef = useRef(false);

  /** Consecutive failure count (for backoff calculation) */
  const failureCountRef = useRef(0);

  /** The flush interval timer ID */
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /** Travel ID ref for use inside timer callback */
  const travelIdRef = useRef(travelId);
  travelIdRef.current = travelId;

  /** Callback refs for stability */
  const onFlushSuccessRef = useRef(onFlushSuccess);
  onFlushSuccessRef.current = onFlushSuccess;

  const onFlushErrorRef = useRef(onFlushError);
  onFlushErrorRef.current = onFlushError;

  /** Enabled ref — read inside timer to check if we should still flush */
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  // ── Core flush logic ──

  /**
   * Attempt to flush the current buffer to the server.
   *
   * @param explicitGpsDistance - If provided, use this instead of gpsDistanceRef
   * @returns true if all points were uploaded successfully
   */
  const flush = useCallback(
    async (explicitGpsDistance?: number): Promise<boolean> => {
      // Guard: nothing to flush
      if (bufferRef.current.length === 0) {
        return true;
      }

      // Guard: no travel ID
      const currentTravelId = travelIdRef.current;
      if (!currentTravelId) {
        return false;
      }

      // Guard: already flushing (prevent concurrent flushes from timer + addPoint)
      if (isFlushingRef.current) {
        return false;
      }

      isFlushingRef.current = true;

      try {
        // Snapshot the buffer — we'll clear only these points on success.
        // New points added during the flush go into the live buffer and
        // will be picked up by the next flush cycle.
        const pointsToFlush = [...bufferRef.current];
        const distance =
          explicitGpsDistance !== undefined
            ? explicitGpsDistance
            : gpsDistanceRef.current;

        // Split into batches if buffer exceeds MAX_BATCH_SIZE (server limit = 50)
        let allSuccess = true;
        let totalFlushed = 0;

        for (let i = 0; i < pointsToFlush.length; i += MAX_BATCH_SIZE) {
          const batch = pointsToFlush.slice(i, i + MAX_BATCH_SIZE);

          // Build the API input — omit null optional fields to minimize payload
          const apiPoints: TrackingPointInput[] = batch.map((p) => {
            const point: TrackingPointInput = {
              latitude: p.lat,
              longitude: p.lng,
              recordedAt: p.recordedAt,
              seq: p.seq,
            };
            if (p.altitude != null) point.altitude = p.altitude;
            if (p.speed != null) point.speed = p.speed;
            if (p.heading != null) point.heading = p.heading;
            if (p.accuracy != null) point.accuracy = p.accuracy;
            return point;
          });

          const result = await TravelTrackingService.addPoints(
            currentTravelId,
            {
              points: apiPoints,
              gpsDistance: distance,
            }
          );

          if (result.code === OP_RESULT_CODES.OK) {
            totalFlushed += batch.length;
          } else {
            // Batch failed — stop sending remaining batches
            allSuccess = false;
            const errorMsg =
              result.errors?.[0]?.errors?.[0] || "Upload failed";
            onFlushErrorRef.current?.(errorMsg);
            break;
          }
        }

        if (totalFlushed > 0) {
          // Remove successfully flushed points from the buffer.
          // Compare by seq to handle points added during the flush.
          const flushedSeqs = new Set(
            pointsToFlush.slice(0, totalFlushed).map((p) => p.seq)
          );
          bufferRef.current = bufferRef.current.filter(
            (p) => !flushedSeqs.has(p.seq)
          );

          // Persist the remaining buffer to IndexedDB
          await storage.setBuffer(bufferRef.current);

          onFlushSuccessRef.current?.(totalFlushed);
        }

        if (allSuccess) {
          // Reset backoff on full success
          failureCountRef.current = 0;
          return true;
        }

        // Partial or full failure — increment backoff
        failureCountRef.current = Math.min(
          failureCountRef.current + 1,
          UPLOAD_BACKOFF_SCHEDULE.length - 1
        );
        return false;
      } catch (err) {
        // Network error or unexpected failure
        failureCountRef.current = Math.min(
          failureCountRef.current + 1,
          UPLOAD_BACKOFF_SCHEDULE.length - 1
        );
        const message =
          err instanceof Error ? err.message : "Network error during upload";
        onFlushErrorRef.current?.(message);
        return false;
      } finally {
        isFlushingRef.current = false;
      }
    },
    [storage]
  );

  // ── Public methods ──

  /**
   * Add a filtered point to the buffer.
   * Triggers immediate flush if buffer reaches MAX_BUFFER_SIZE.
   */
  const addPoint = useCallback(
    (point: BufferedTrackingPoint): void => {
      bufferRef.current.push(point);

      // Mirror to IndexedDB asynchronously (fire-and-forget).
      // If this fails, the in-memory buffer is still the source of truth.
      storage.setBuffer(bufferRef.current).catch(() => {
        // Silently ignore — IndexedDB persistence is best-effort
      });

      // Trigger early flush if buffer is getting large
      if (
        bufferRef.current.length >= MAX_BUFFER_SIZE &&
        !isFlushingRef.current
      ) {
        flush();
      }
    },
    [storage, flush]
  );

  /**
   * Flush all buffered points immediately (synchronous drain).
   */
  const flushNow = useCallback(
    async (gpsDistance: number): Promise<boolean> => {
      // Wait for any in-progress flush to complete
      // Simple spin-wait with async yield (at most a few ms)
      let waitAttempts = 0;
      while (isFlushingRef.current && waitAttempts < 50) {
        await new Promise((resolve) => setTimeout(resolve, 50));
        waitAttempts++;
      }

      return flush(gpsDistance);
    },
    [flush]
  );

  /**
   * Get current buffer size.
   */
  const getBufferSize = useCallback((): number => {
    return bufferRef.current.length;
  }, []);

  /**
   * Restore buffer from IndexedDB on session recovery.
   */
  const restoreBuffer = useCallback(async (): Promise<
    BufferedTrackingPoint[]
  > => {
    try {
      const restored = await storage.getBuffer();
      if (restored.length > 0) {
        bufferRef.current = restored;
      }
      return bufferRef.current;
    } catch {
      return bufferRef.current;
    }
  }, [storage]);

  /**
   * Clear the buffer completely.
   */
  const clearBuffer = useCallback(async (): Promise<void> => {
    bufferRef.current = [];
    await storage.clearBuffer();
  }, [storage]);

  /**
   * Check if an upload is in progress.
   */
  const isUploading = useCallback((): boolean => {
    return isFlushingRef.current;
  }, []);

  /**
   * Update the GPS distance value for the next flush.
   */
  const setGpsDistance = useCallback((distance: number): void => {
    gpsDistanceRef.current = distance;
  }, []);

  // ── Flush timer management ──

  useEffect(() => {
    // Clear any existing timer
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (!enabled || !travelId) {
      return;
    }

    /**
     * Timer callback: flush if buffer is non-empty and we're still enabled.
     *
     * The interval adapts based on backoff:
     * - On success (failureCount=0): FLUSH_INTERVAL_MS (10s)
     * - On failures: UPLOAD_BACKOFF_SCHEDULE[failureCount] (10s → 20s → 30s)
     *
     * We implement backoff by skipping flush cycles rather than changing
     * the timer interval, which is simpler and avoids timer thrashing.
     */
    let tickCount = 0;

    timerRef.current = setInterval(() => {
      tickCount++;

      // Check if we're still supposed to be running
      if (!enabledRef.current || !travelIdRef.current) {
        return;
      }

      // Calculate how many ticks to skip based on backoff
      const currentBackoff =
        UPLOAD_BACKOFF_SCHEDULE[failureCountRef.current] ?? FLUSH_INTERVAL_MS;
      const ticksToSkip = Math.round(currentBackoff / FLUSH_INTERVAL_MS);

      if (tickCount % ticksToSkip !== 0) {
        return; // Skip this tick (backoff)
      }

      // Flush if there are buffered points
      if (bufferRef.current.length > 0 && !isFlushingRef.current) {
        flush();
      }
    }, FLUSH_INTERVAL_MS);

    return () => {
      if (timerRef.current !== null) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [enabled, travelId, flush]);

  return {
    addPoint,
    flushNow,
    getBufferSize,
    restoreBuffer,
    clearBuffer,
    isUploading,
    setGpsDistance,
  };
};